import { DIODE, FERRITE, IND, JUMPER, SWITCH, RES, PMIC, CAP, LOAD, IGNORE, TRANSISTOR, BUCK_CONVERTER, CONNECTOR, ROOT, POWER_SWITCH } from "../../../../constants/componentType";
import { splitArrayToArrays } from "../../arrayHelper";
import { newNanoId } from "../../idHelper";
import { checkRLCValueInPowerTree, getValueByComp } from "../../RLCValue";

const MIDDLE_COMP = [SWITCH, IND, RES, DIODE, JUMPER, FERRITE, TRANSISTOR];
const NONET = 'NONET', GPIO = 'GPIO', I2C = 'I2C', GND = 'GND';

class PowerTreeMaker {
  constructor(params) {
    const {
      key, compName, powerNet, groundNets, designId, setting, table, pinMap, pinConnection, doNotStuff,
      layoutComponents, layoutNets, useInput, powerTreeLevel, getTreeComponentGnd, broComponent, connector,
      isConnectTree, logs = [], filterNets = [] } = params;

    // input
    this.powerNet = powerNet;
    this.groundNets = groundNets;
    this.setting = setting;
    this.table = table;
    this.pinMap = pinMap;
    this.pinConnection = pinConnection;
    this.doNotStuff = doNotStuff || [];
    this.layoutComponents = layoutComponents;
    this.layoutNets = layoutNets;
    this.useInput = useInput;
    this.powerTreeLevel = Number(powerTreeLevel) || 8;
    this.broComponent = broComponent; // for buck converter
    this.connector = connector;
    this.isConnectTree = isConnectTree;
    this.noTraceNets = [...filterNets]

    // input fn
    this.getTreeComponentGnd = getTreeComponentGnd;

    // temp
    this.groundPoints = {};
    this.usedNets = [];
    this.strictMode = false;

    // output
    this.key = key;
    this.designId = designId;
    this.root = compName;
    this.name = `${powerNet}-${compName}`
    this.logs = [...logs];
    this.paths = [];
    this.gndPaths = [];
    this.filterTree = [];
  }

  generatePowerTrace = () => {
    const { root, powerNet, groundNets, layoutComponents, broComponent, isConnectTree, key } = this;
    const component = layoutComponents.get(root);
    if (!component) {
      this.outputInfo()
      return;
    }
    const { partName } = component
    const pins = [...component.pins.values()].filter(pin => [powerNet, ...groundNets].includes(pin.net));
    let broComp = undefined;
    if (broComponent) {
      const bro = layoutComponents.get(broComponent);
      if (bro) {
        const broPins = [...bro.pins.values()].filter(pin => [powerNet, ...groundNets].includes(pin.net));
        broComp = {
          name: bro.name,
          pins: broPins,
          partName: bro.partName
        }
      }
    }
    const rootComp = isConnectTree ?
      new ConnectorComponent({ name: root, pins, type: CONNECTOR, part: partName, gnds: [...groundNets], broComp, key, resistance: '50' })
      : new RootComponent({ name: root, pins, type: ROOT, part: partName, gnds: [...groundNets], broComp, key });
    this.logs.push({ text: `\t\t- Power Trace:`, type: 'title', key: this.name })
    this.trace({
      level: 1,
      prevComp: JSON.parse(JSON.stringify(rootComp)),
      net: powerNet,
      path: [],
      rootId: rootComp.id,
      tempComps: [],
      prevGnds: [...groundNets]
    })

    if (this.paths.length > 500) {
      this.usedNets = [];
      this.logs = []
      this.logs.push({ text: `\t- ${this.paths.length} Paths were found, which is too many. Increase the limit and search again.`, type: 'warning', key: this.name })
      this.logs.push({ text: `\t\t- Power Trace:`, type: 'title', key: this.name });
      this.paths = [];
      this.strictMode = true;
      this.trace({
        level: 1,
        prevComp: JSON.parse(JSON.stringify(rootComp)),
        net: powerNet,
        path: [],
        rootId: rootComp.id,
        tempComps: [],
        prevGnds: [...groundNets],

      })
      this.strictMode = false;
    }

    if (!this.paths.length) {
      this.paths.push([rootComp])
    }
  }

  outputInfo = () => {
    const { name, logs, paths, gndPaths, filterTree, key, designId, root } = this;
    return {
      key,
      designId,
      root,
      name,
      logs,
      paths,
      gndPaths,
      filterTree
    }
  }

  getShortestPath = () => {
    const { paths } = this
    const _path = paths.reduce((path, cur) => {
      if (!path) return cur;
      const minLen = path.length;
      const curLen = cur.length;
      const maxPinsLen = path[minLen - 1].pins ? path[minLen - 1].pins.length : 0;
      const curPinsLen = cur[curLen - 1].pins ? cur[curLen - 1].pins.length : 0;
      if (curLen < minLen || (curLen === minLen && curPinsLen > maxPinsLen)) {
        return cur;
      }
      return path;
    }, null);
    return _path
  }

  trace = ({ level, prevComp, net, path, rootId, tempComps = [], prevGnds }) => {
    const { powerTreeLevel, layoutNets, layoutComponents, doNotStuff, groundNets, isConnectTree, root, key, noTraceNets, strictMode, usedNets } = this;
    if (level > powerTreeLevel) {
      this.logs.push({ text: `\t\tX Maximum search depth exceeded.`, type: 'error', key: this.name })
      return;
    }

    const prevCompName = prevComp.name;
    const netInfo = layoutNets.get(net);

    if (!netInfo || noTraceNets.includes(net)) {
      return;
    }
    const newComponents = [...netInfo.pins.keys()].map(compName => {
      if (isConnectTree && compName === root) {
        return null
      }

      if (doNotStuff.includes(compName)) {
        this.logs.push({ text: `\t\tX Skip ${compName}. Component is DNS.`, type: 'error', key: this.name })
        return null
      }

      if ([prevCompName].includes(compName)) {
        return null
      }

      if (tempComps.includes(compName)) {
        return null
      }

      const compInfo = layoutComponents.get(compName);
      const type = this.getComponentType(compInfo);
      return { compInfo, type, compName }
    }).filter(item => !!item);
    const _tempComps = [...new Set([...tempComps, ...newComponents.filter(item => MIDDLE_COMP.includes(item.type)).map(item => item.compName)])]
    let tempNets = [net, ...path.map(item => item.prevNet).filter(item => !!item)];
    if (strictMode) {
      tempNets = [...new Set([...usedNets, ...tempNets])]
      usedNets.push(...newComponents.filter(item => MIDDLE_COMP.includes(item.type)).map(item => [...item.compInfo.pins.values()].map(pin => pin.net)).flat())
      this.usedNets = [...new Set(usedNets)]
    }
    let exsitTransistor = [];
    for (let component of newComponents) {
      const { compInfo, type, compName } = component;

      if (exsitTransistor.includes(compName)) {
        continue;
      }

      if (MIDDLE_COMP.includes(type)) {
        const resistance = this.getComponentRes(compInfo, type);

        if (isNaN(Number(resistance)) || Number(resistance) >= 1e5) {
          this.logs.push({ text: `\t\tX Skip ${compName}. Resistance value ${resistance} is greater than tracing threshold value (1e5mΩ).`, type: 'error', key: this.name })
          continue;
        }

        const pins = [...compInfo.pins.values()];
        const nextNets = this.getTraceNets(compName, type, pins, compInfo.partName, net, tempNets, [...groundNets, NONET]);
        if (!nextNets.length) {
          this.logs.push({ text: `\t\tX Skip ${compName}. There is no legal next net.`, type: 'error', key: this.name })
        }
        for (let nextNet of nextNets) {
          if (strictMode && nextNet.includes(GND)) {
            continue;
          }
          const middleComp = new MiddleComponent({
            name: compName,
            type,
            part: compInfo.partName,
            prevComp: prevComp.id,
            prevNet: net,
            pins: pins.filter(pin => [nextNet, net].includes(pin.net)),
            resistance,
            key,
            rootId
          });
          const _prevComp = { ...prevComp, nextNet: net, nextComp: middleComp.id }
          this.trace({
            level: level + 1,
            prevComp: JSON.parse(JSON.stringify(middleComp)),
            net: nextNet,
            path: JSON.parse(JSON.stringify([...path, _prevComp])),
            rootId,
            tempComps: _tempComps,
            prevGnds
          })
        }
      }

      if (type === BUCK_CONVERTER) {
        const broCompName = this.getBuckConverterBroComp(compName);
        broCompName && exsitTransistor.push(broCompName)
        this.buckConverterCheck({
          pmic: compInfo,
          prevComp: prevComp,
          net,
          tempComps: _tempComps,
          path: JSON.parse(JSON.stringify([...path])),
          rootId,
          prevGnds
        })
      }

      if (type === PMIC) {
        this.pmicCheck({
          pmic: compInfo,
          prevComp: prevComp,
          net,
          tempComps: _tempComps,
          path: JSON.parse(JSON.stringify([...path])),
          rootId,
          prevGnds
        })
      }

      if ([LOAD, CONNECTOR].includes(type)) {
        this.loadCheck({
          compName,
          prevComp,
          net: net,
          path: JSON.parse(JSON.stringify([...path])),
          rootId,
          type: type === CONNECTOR ? CONNECTOR : LOAD,
          prevGnds
        })
      }

      if (type === IGNORE) {
        this.logs.push({ text: `\t\tX Skip ${compName}. Too few pins.`, type: 'error', key: this.name })
      }
    }
  }

  loadCheck = ({ compName, prevComp, net, path, rootId, type = LOAD, broCompName, prevGnds }) => {
    const { layoutComponents, key } = this;
    const compInfo = layoutComponents.get(compName);
    if (!compInfo) {
      return;
    }

    const pins = [...compInfo.pins.values()].filter(pin => pin.net === net);
    let broComp = undefined;
    if (broCompName) {
      const bro = layoutComponents.get(broCompName);
      if (bro) {
        const broPins = [...bro.pins.values()].filter(pin => [net].includes(pin.net));
        broComp = {
          name: bro.name,
          pins: broPins,
          partName: bro.partName
        }
      }
    }
    const gnds = this.groundCheck({ name: compName, type, broComp: broCompName, net, prevGnds })
    const load = type === CONNECTOR ? new ConnectorComponent({
      name: compName,
      type,
      part: compInfo.partName,
      prevComp: prevComp.id,
      prevNet: net,
      pins,
      gnds,
      rootId,
      broComp,
      key,
      resistance: '50'
    })
      : new LoadComponent({
        name: compName,
        type,
        part: compInfo.partName,
        prevComp: prevComp.id,
        prevNet: net,
        pins,
        gnds,
        rootId,
        broComp,
        key
      });
    this.logs.push({ text: `\t\t√ ${compName} - ${type}`, type: 'success', key: this.name })
    const _prevComp = { ...prevComp, nextNet: net, nextComp: load.id }
    const pathId = newNanoId(8, 'number');
    this.paths.push([...path, _prevComp, load].map(comp => ({ ...comp, pathId })));
  }

  pmicCheck = ({ pmic, tempComps, path, prevComp, net, rootId, prevGnds }) => {
    const { pinMap, useInput, key } = this;
    const { name, partName, pins } = pmic;

    let pmicPinMap = pinMap.find(map => map.partNumber === partName && map.components && map.components.includes(name));
    if (!pmicPinMap) {
      pmicPinMap = pinMap.find(map => map.partNumber === partName);
    }

    if (pmicPinMap) {
      const input = [...pins.values()].filter(pin => pin.net === net).map(pin => pin.pinName);
      let isInput = false;
      if (pmicPinMap.data) {
        const inputRow = pmicPinMap.data.find(row => row.input.some(pinName => input.includes(pinName)));
        if (inputRow) {
          isInput = true;
          if (inputRow.output && inputRow.output.length) {
            const nextNets = [...new Set([...pins.values()].filter(pin => inputRow.output.includes(pin.pinName)).map(pin => pin.net))]
            for (let nextNet of nextNets) {
              const gnds = this.groundCheck({ name, type: PMIC, prevGnds })
              const pmicComp = new PMICComponent({ name, type: PMIC, part: partName, prevComp: prevComp.id, prevNet: net, pins: [...pins.values()].filter(pin => [nextNet, net].includes(pin.net)), gnds, rootId, key });
              const _prevComp = { ...prevComp, nextNet: net, nextComp: pmicComp.id }
              this.filterTree.push(`${nextNet}-${name}`)
              this.logs.push({ text: `\t\t√ ${name} - ${PMIC} - ${nextNet}`, type: 'success', key: this.name })
              this.trace({
                level: 1,
                prevComp: JSON.parse(JSON.stringify(pmicComp)),
                net: nextNet,
                tempComps,
                path: JSON.parse(JSON.stringify([...path, _prevComp])),
                rootId: pmicComp.id,
                prevGnds: gnds
              })
            }
          } else {
            this.loadCheck({
              compName: name,
              prevComp,
              net: net,
              path: JSON.parse(JSON.stringify([...path])),
              rootId,
              type: PMIC,
              prevGnds
            })
          }
        }
      }
      if (!isInput && !useInput) {
        this.loadCheck({
          compName: name,
          prevComp,
          net: net,
          path: JSON.parse(JSON.stringify([...path])),
          rootId,
          type: PMIC,
          prevGnds
        })
      } else if (!isInput && useInput) {
        this.logs.push({ text: `\t\tX Skip ${name}. Pin ${input.join(', ')} is not specified as PMIC input.  Close <Only trace to PMICs through input pins> option in Options or review PMIC map or set as Load.`, type: 'error', key: this.name })
      }
    }
  }

  buckConverterCheck = ({ pmic, tempComps, path, prevComp, net, rootId, prevGnds }) => {
    const { pinMap, useInput, layoutComponents, key } = this;
    const { name, partName, pins } = pmic;

    const pmicPinMap = pinMap.find(map => map.partNumber.includes(name) && map.type === BUCK_CONVERTER);

    if (pmicPinMap) {
      const broCompName = pmicPinMap.partNumber.split(' - ').find(compName => compName !== name);
      const bro = layoutComponents.get(broCompName);
      const input = [...pins.values()].filter(pin => pin.net === net).map(pin => pin.pinName);
      let isInput = false;
      if (pmicPinMap.data) {
        const inputRow = pmicPinMap.data.find(row => row.input.map(pinName => pinName.replace(`${name}_`, '')).some(pinName => input.includes(pinName)));
        if (inputRow) {
          isInput = true;
          if (inputRow.output && inputRow.output.length) {
            const output = inputRow.output.map(pinName => pinName.replace(`${name}_`, ''))
            const broOutput = inputRow.output.map(pinName => pinName.replace(`${broCompName}_`, ''))
            const nextNets = [...new Set([...pins.values()].filter(pin => output.includes(pin.pinName)).map(pin => pin.net))];
            const broNextNets = [...new Set([...bro.pins.values()].filter(pin => broOutput.includes(pin.pinName)).map(pin => pin.net))];
            for (let nextNet of [...new Set([...nextNets, ...broNextNets])]) {
              const broComp = {
                name: bro.name,
                pins: [...bro.pins.values()].filter(pin => [nextNet, net].includes(pin.net)),
                partName: bro.partName
              }
              const gnds = this.groundCheck({ name, type: PMIC, broComp: broCompName, prevGnds });
              const pmicComp = new PMICComponent({ name, type: PMIC, part: partName, prevComp: prevComp.id, prevNet: net, pins: [...pins.values()].filter(pin => [nextNet, net].includes(pin.net)), gnds, rootId, broComp, key });
              const _prevComp = { ...prevComp, nextNet: net, nextComp: pmicComp.id }
              this.filterTree.push(`${nextNet}-${name}`)
              this.trace({
                level: 1,
                prevComp: JSON.parse(JSON.stringify(pmicComp)),
                net: nextNet,
                tempComps,
                path: JSON.parse(JSON.stringify([...path, _prevComp])),
                rootId: pmicComp.id,
                prevGnds: gnds
              })
            }
          } else {
            this.loadCheck({
              compName: name,
              prevComp,
              net: net,
              path: JSON.parse(JSON.stringify([...path])),
              rootId,
              type: PMIC,
              broCompName,
              prevGnds
            })
          }
        }
      }
      if (!isInput && !useInput) {
        this.loadCheck({
          compName: name,
          prevComp,
          net: net,
          path: JSON.parse(JSON.stringify([...path])),
          rootId,
          type: PMIC,
          broCompName,
          prevGnds
        })
      } else {
        this.logs.push({ text: `\t\tX Skip ${name}. Pin ${input.join(', ')} is not specified as PMIC input.  Close <Only trace to PMICs through input pins> option in Options or review PMIC map or set as Load.`, type: 'error', key: this.name })
      }
    }
  }

  generateGround = () => {
    const { paths, layoutComponents, key, isConnectTree, root } = this;
    let groundPoints = [], groundStarts = [], filterNets = [];
    const TARGET_TYPES = new Set([LOAD, PMIC, CONNECTOR]);
    for (const path of paths) {
      let currentStart = path[0];
      for (let i = 1; i < path.length; i++) {
        const component = path[i];
        filterNets.push(component.prevNet)
        if (!TARGET_TYPES.has(component.type)) continue;
        const connection = {
          startId: currentStart.id,
          startName: currentStart.name,
          startNets: currentStart.gnds,
          endId: component.id,
          endName: component.name,
          endNets: component.gnds
        };
        groundPoints.push(connection);
        groundStarts.push(currentStart.id);
        currentStart = component;
      }
    }

    filterNets = [...new Set(filterNets)]
    for (let startId of [...new Set(groundStarts)]) {
      const points = groundPoints.filter(item => item.startId === startId);
      this.groundPoints[startId] = points
      const rootName = points[0].startName, rootNets = points[0].startNets;

      const component = layoutComponents.get(rootName);
      if (!component) {
        continue;
      }
      const { partName } = component;
      const { root, broComponent } = this;

      for (let rootNet of rootNets) {
        let broComp = undefined;
        if (broComponent && root === rootName) {
          const bro = layoutComponents.get(broComponent);
          if (bro) {
            const broPins = [...bro.pins.values()].filter(pin => [rootNet].includes(pin.net));
            broComp = {
              name: bro.name,
              pins: broPins,
              partName: bro.partName
            }
          }
        } else if (component.type === TRANSISTOR) {
          const broCompName = this.getBuckConverterBroComp(rootName);
          if (broCompName) {
            const bro = layoutComponents.get(broCompName);
            if (bro) {
              const broPins = [...bro.pins.values()].filter(pin => [rootNet].includes(pin.net));
              broComp = {
                name: bro.name,
                pins: broPins,
                partName: bro.partName
              }
            }
          }
        }
        const pins = [...component.pins.values()].filter(pin => [rootNet].includes(pin.net));
        const rootComp = isConnectTree ?
          rootName === root ? new ConnectorComponent({ name: rootName, pins, type: CONNECTOR, part: partName, gnds: [...rootNets], isGnd: true, powerId: startId, broComp, key, resistance: '5' })
            : new RootComponent({ name: rootName, pins, type: PMIC, part: partName, gnds: [...rootNets], isGnd: true, powerId: startId, broComp, key })
          : new RootComponent({ name: rootName, pins, type: rootName === root ? ROOT : PMIC, part: partName, gnds: [...rootNets], isGnd: true, powerId: startId, broComp, key });
        this.logs.push({ text: `\t\t- Ground Trace (${rootName}):`, type: 'title', key: this.name })
        this.groundTrace({
          level: 1,
          prevComp: JSON.parse(JSON.stringify(rootComp)),
          net: rootNet,
          tempNets: [rootNet],
          path: [],
          rootId: rootComp.id,
          powerId: startId,
          filterNets
        })
      }
    }
  }

  groundTrace = ({ level, prevComp, net, tempNets, path, rootId, powerId, filterNets }) => {
    const { powerTreeLevel, layoutNets, layoutComponents, doNotStuff, groundPoints, key, noTraceNets } = this;
    if (level > powerTreeLevel) {
      return;
    }

    if (!groundPoints[powerId] || !groundPoints[powerId].length) {
      return;
    }

    const prevCompName = prevComp.name;
    const netInfo = layoutNets.get(net);

    if (!netInfo || noTraceNets.includes(net)) {
      return;
    }
    const newComponents = [...netInfo.pins.keys()];

    const { trueArray: traced, falseArray: needTrace } = splitArrayToArrays({ array: this.groundPoints[powerId], rule: item => newComponents.includes(item.endName) && item.endNets.includes(net) });
    this.groundPoints[powerId] = needTrace;
    if (traced.length) {
      const comps = [...new Set(traced.map(item => item.endName))];
      for (let comp of comps) {
        this.groundLoadCheck({
          compName: comp,
          prevComp,
          net: net,
          path: JSON.parse(JSON.stringify([...path])),
          rootId,
        })
      }
    }

    if (!needTrace.length) {
      return;
    }

    const _tempNets = [...new Set([...tempNets, ...newComponents.map(comp => layoutComponents.get(comp)).filter(comp => !!comp && MIDDLE_COMP.includes(comp.type)).map(comp => [...comp.pins.values()].map(pin => pin.net)).flat()])]
    for (let compName of newComponents) {
      if (doNotStuff.includes(compName)) {
        this.logs.push({ text: `\t\tX Skip ${compName}. Component is DNS.`, type: 'error', key: this.name })
        continue;
      }

      if ([prevCompName].includes(compName)) {
        continue;
      }

      const compInfo = layoutComponents.get(compName);
      const type = this.getComponentType(compInfo);

      if (MIDDLE_COMP.includes(type)) {
        const resistance = this.getComponentRes(compInfo, type);

        if (isNaN(Number(resistance)) || Number(resistance) >= 1e5) {
          this.logs.push({ text: `\t\tX Skip ${compName}. Resistance value ${resistance} is greater than tracing threshold value (1e5mΩ).`, type: 'error', key: this.name })
          continue;
        }

        const pins = [...compInfo.pins.values()];
        const nextNets = this.getTraceNets(compName, type, pins, compInfo.partName, net, tempNets, [...filterNets, NONET]);
        if (!nextNets.length) {
          this.logs.push({ text: `\t\tX Skip ${compName}. There is no legal next net.`, type: 'error', key: this.name })
        }
        for (let nextNet of nextNets) {
          const middleComp = new MiddleComponent({
            name: compName,
            type,
            part: compInfo.partName,
            prevComp: prevComp.id,
            prevNet: net,
            pins: pins.filter(pin => [nextNet, net].includes(pin.net)),
            resistance,
            isGnd: true,
            key,
            rootId
          });
          const _prevComp = { ...prevComp, nextNet: net, nextComp: middleComp.id }
          this.groundTrace({
            level: level + 1,
            prevComp: JSON.parse(JSON.stringify(middleComp)),
            net: nextNet,
            tempNets: [..._tempNets],
            path: JSON.parse(JSON.stringify([...path, _prevComp])),
            rootId,
            powerId,
            filterNets
          })
        }
      }
    }
  }

  groundLoadCheck = ({ compName, prevComp, net, path, rootId }) => {
    const { layoutComponents, key } = this;
    const compInfo = layoutComponents.get(compName);
    if (!compInfo) {
      return;
    }
    const type = this.getComponentType(compInfo);
    let broComp = null;
    if (type === BUCK_CONVERTER) {
      const { pinMap } = this;
      const currentPinMap = pinMap.find(map => map.partNumber.includes(compName) && map.type === BUCK_CONVERTER);
      if (currentPinMap) {
        const comps = currentPinMap.partNumber.split(' - ');
        const broCompName = comps.find(comp => comp !== compName);
        const bro = layoutComponents.get(broCompName);
        if (bro) {
          const broPins = [...bro.pins.values()].filter(pin => [net].includes(pin.net));
          broComp = {
            name: bro.name,
            pins: broPins,
            partName: bro.partName
          }
        }
      }
    }
    const pins = [...compInfo.pins.values()].filter(pin => pin.net === net);
    const load = type === CONNECTOR ? new ConnectorComponent({ name: compName, type, part: compInfo.partName, prevComp: prevComp.id, prevNet: net, pins, gnds: [net], rootId, isGnd: true, broComp, key, resistance: '5' })
      : new LoadComponent({ name: compName, type, part: compInfo.partName, prevComp: prevComp.id, prevNet: net, pins, gnds: [net], rootId, isGnd: true, broComp, key });
    this.logs.push({ text: `\t\t√ ${compName} - ${type}`, type: 'success', key: this.name })
    const _prevComp = { ...prevComp, nextNet: net, nextComp: load.id }
    const pathId = newNanoId(8, 'number');
    this.gndPaths.push([...path, _prevComp, load].map(comp => ({ ...comp, pathId })));
  }

  groundCheck = ({ name, type, broComp, net, prevGnds = [] }) => {
    const { layoutComponents, pinMap, groundNets, designId } = this;
    const compInfo = layoutComponents.get(name);
    if (!compInfo) {
      return groundNets;
    }
    let newGrounds = prevGnds && prevGnds.length ? [...prevGnds] : [...groundNets];
    for (let ground of (prevGnds && prevGnds.length ? [...prevGnds] : [...groundNets])) {
      newGrounds = this.getTreeComponentGnd(compInfo, designId, pinMap, broComp, ground, net ? [net] : []);
      if (newGrounds.some(gnd => groundNets.includes(gnd))) {
        break;
      }
    }
    if (type === CONNECTOR) {
      newGrounds = this.connectorGroundCheck(name, net, newGrounds);
    }
    return newGrounds
  }

  connectorGroundCheck = (name, prevNet, grounds) => {
    const { connector } = this;
    const component = connector.components.find(item => item.name === name);
    const nets = component.next.map(nextComp => nextComp.nets.map(connectNets => connectNets[0])).flat();
    const groundNets = nets.filter(net => net !== prevNet);
    if (!groundNets.length) {
      return grounds
    } else if (groundNets.length === 1) {
      return groundNets
    } else {
      const _groundNets = groundNets.filter(net => grounds.includes(net));
      return _groundNets.length ? _groundNets : groundNets;
    }
  }

  getComponentType = (compInfo) => {
    const { type, pmicType, name, pins, partName } = compInfo;
    const { setting, pinMap, connector, pinConnection, root } = this;
    const { Load = ['U'] } = setting;

    if (connector && connector.components && connector.components.find(comp => comp.name === name)) {
      return CONNECTOR
    }

    if (Load.includes(name)) {
      return LOAD;
    }

    if (type === TRANSISTOR) {
      if (pinMap.some(map => map.partNumber.includes(name))) {
        return BUCK_CONVERTER;
      }
      return TRANSISTOR
    }

    if (pmicType || pinMap.some(map => map.partNumber === partName && (!map.components || !map.components.length || map.components.includes(name)))) {

      // For special case where PMIC is also a power switch (Apple)
      if (name === root && type === SWITCH && pinConnection.find(item => item.partNumber === partName && item.type === POWER_SWITCH)) {
        return SWITCH;
      }

      return PMIC
    }

    if (MIDDLE_COMP.includes(type)) {
      return type;
    }

    if (pinConnection.find(item => item.partNumber === partName && item.type === POWER_SWITCH)) {
      return SWITCH;
    }

    if (type === CAP) {
      return type;
    }

    const txt = Load.join('|');
    const regObj = new RegExp(`^(${txt})`, 'ig');
    const words = name.match(regObj);
    if (words && words.length) {
      return LOAD;
    }

    if (type === IGNORE && pins.size > 4) {
      return LOAD
    }
    return type
  }

  getComponentRes = (compInfo, type) => {
    const { table } = this;
    const { partName, value: defaultValue } = compInfo;
    const tablePart = table.find(row => row.part === partName);
    let value = tablePart ? getValueByComp(tablePart) : type === IND ? 0 : checkRLCValueInPowerTree(defaultValue);
    if (Number(value) === 0 && type === FERRITE) {
      value = 55;
    }
    return value;
  }

  getTraceNets = (compName, type, pins, part, net, tempNets = [], filterNets = []) => {
    return this.getTraceNextNets(compName, type, pins, part, net, tempNets, filterNets).filter(net => net !== NONET && !net.includes(GPIO) && !net.includes(I2C))
  }

  getTraceNextNets = (compName, type, pins, part, net, tempNets, filterNets) => {
    const { pinConnection, root, powerNet } = this;

    if ([IND, FERRITE, JUMPER].includes(type)) {
      return [...new Set(pins.map(pin => pin.net))].filter(_net => ![...filterNets, ...tempNets].includes(_net));
    }

    if ([DIODE].includes(type)) {
      const { trueArray: prev, falseArray: next } = splitArrayToArrays({ array: pins, rule: pin => pin.net === net });
      if (!prev.some(item => ['A', 'A1'].includes(item.pin)) || next.some(item => [...filterNets].includes(item.net))) {
        this.logs.push({ text: `\t\tX Skip ${compName}. Two pins are not connected to Power Net at the same time or the pins are illegal.`, type: 'error', key: this.name })
        return []
      } else {
        return [...new Set(next.filter(pin => ![...filterNets].includes(pin.net)).map(pin => pin.net))]
      }
    }

    if ([RES, SWITCH].includes(type) && pins.length > 2) {
      const _pins = pins.filter(pin => pin.net !== NONET && !pin.net.includes(GPIO) && !pin.net.includes(I2C));
      if (_pins.length <= 2) {
        return [...new Set(pins.map(pin => pin.net))].filter(_net => ![...filterNets, ...tempNets].includes(_net));
      }
      const pinConn = pinConnection.find(item => item.partNumber === part);
      if (!pinConn || !pinConn.pinMap || !pinConn.pinMap.length) {
        let nextPins = [], notLimit = false;
        this.logs.push({ text: `\t\t? ${compName} is missing Pin Map. Go to Component Setting -> Classfication -> ${type === SWITCH ? ' Power Switch' : 'Multi Resistor'}  for Pin Map setting.`, type: 'warning', key: this.name })
        if (type === SWITCH) {
          if (compName === root) {
            notLimit = true;
          }
          if (!notLimit) {
            const out = pins.filter(pin => pin.pinName.toUpperCase().includes('OUT') && ![...filterNets].includes(pin.net));
            if (out.length) {
              const prevPins = pins.filter(pin => pin.net === net).map(pin => pin.pinName);
              if (prevPins.some(pinName => pinName.toUpperCase().includes('IN'))) {
                nextPins.push(...out.map(pin => pin.pin))
              } else {
                notLimit = true;
              }
            }
          }
        }
        if (!notLimit) {
          if (!nextPins.length) {
            const { trueArray: prev, falseArray: next } = splitArrayToArrays({ array: pins, rule: pin => pin.net === net })
            for (let cur of prev) {
              if (!isNaN(Number(cur.pin))) {
                let nextNum = pins.length + 1 - Number(cur.pin);
                const nextPin = next.find(pin => pin.pin === String(nextNum));
                if (nextPin && ![...filterNets].includes(nextPin.net)) {
                  nextPins.push(String(nextNum))
                }
              }
            }
          }
          return [...new Set(pins.filter(pin => nextPins.includes(pin.pin)).map(pin => pin.net))];
        } else {
          return []
        }
      } else {
        const prevPins = pins.filter(pin => pin.net === net).map(pin => pin.pin);
        let nextPins = [];
        pinConn.pinMap.forEach(array => {
          const left = array[0] ? array[0].split(', ') : [];
          const right = array[1] ? array[1].split(', ') : [];
          if (left.some(item => prevPins.includes(item))) {
            nextPins.push(...right);
          } else if (right.some(item => prevPins.includes(item))) {
            nextPins.push(...left);
          }
        })
        pins = pins.filter(item => nextPins.includes(item.pin));
        const nextNets = [...new Set(pins.map(pin => pin.net))];
        if (compName === root) {
          return nextNets.filter(net => net !== powerNet)
        }
        return nextNets;
      }
    }

    return [...new Set(pins.map(pin => pin.net))].filter(_net => ![...filterNets, ...tempNets].includes(_net));
  }

  getBuckConverterBroComp = (name) => {
    const pmicPinMap = this.pinMap.find(map => map.partNumber.includes(name) && map.type === BUCK_CONVERTER);

    if (pmicPinMap) {
      const broCompName = pmicPinMap.partNumber.split(' - ').find(compName => compName !== name);
      return broCompName
    }
    return null
  }

  generateEditTrace = () => {
    const { root, powerNet, groundNets, layoutComponents, broComponent, key } = this;
    const component = layoutComponents.get(root);
    if (!component) {
      this.outputInfo()
      return;
    }
    const type = this.getComponentType(component)
    const { partName } = component
    const pins = [...component.pins.values()].filter(pin => [powerNet, ...groundNets].includes(pin.net));
    let broComp = undefined;
    if (broComponent) {
      const bro = layoutComponents.get(broComponent);
      if (bro) {
        const broPins = [...bro.pins.values()].filter(pin => [powerNet, ...groundNets].includes(pin.net));
        broComp = {
          name: bro.name,
          pins: broPins,
          partName: bro.partName
        }
      }
    }
    const gndNet = this.groundCheck({ name: root, type })
    const rootComp = type === LOAD || type === IGNORE ? new LoadComponent({ name: root, type: LOAD, part: component.partName, pins, gnds: gndNet, broComp, key }) :
      type === CONNECTOR ? new ConnectorComponent({ name: root, pins, type: CONNECTOR, part: partName, gnds: gndNet, broComp, key, resistance: '50' }) :
        type === PMIC ? new PMICComponent({ name: root, type: PMIC, part: partName, gnds: gndNet, key }) :
          new MiddleComponent({ name: root, type, part: component.partName, resistance: this.getComponentRes(component, type) || '0', key });;
    if (![IGNORE, LOAD, CONNECTOR].includes(type)) {
      this.logs.push({ text: `\t\t- Power Trace:`, type: 'title', key: this.name })
      this.trace({
        level: 1,
        prevComp: JSON.parse(JSON.stringify(rootComp)),
        net: powerNet,
        tempNets: [powerNet],
        path: [],
        rootId: rootComp.id
      })
    }
  }

  generateRootTrace = () => {
    const { root, powerNet, groundNets, layoutComponents, broComponent, key } = this;
    const component = layoutComponents.get(root);
    if (!component) {
      this.outputInfo()
      return;
    }
    const type = this.getComponentType(component)
    const { partName } = component
    const pins = [...component.pins.values()].filter(pin => [powerNet, ...groundNets].includes(pin.net));
    let broComp = undefined;
    if (broComponent) {
      const bro = layoutComponents.get(broComponent);
      if (bro) {
        const broPins = [...bro.pins.values()].filter(pin => [powerNet, ...groundNets].includes(pin.net));
        broComp = {
          name: bro.name,
          pins: broPins,
          partName: bro.partName
        }
      }
    }
    const gndNet = this.groundCheck({ name: root, type })
    const rootComp = type === LOAD || type === IGNORE ? new LoadComponent({ name: root, type, part: component.partName, pins, gnds: gndNet, broComp, key }) :
      type === CONNECTOR ? new ConnectorComponent({ name: root, pins, type: CONNECTOR, part: partName, gnds: gndNet, broComp, key, resistance: '50' }) :
        type === PMIC ? new PMICComponent({ name: root, type: PMIC, part: partName, gnds: gndNet, key }) :
          new MiddleComponent({ name: root, type, part: component.partName, resistance: this.getComponentRes(component, type) || '0', key });;
    this.paths.push([JSON.parse(JSON.stringify(rootComp))])
  }
}

class TreeComponent {
  constructor(props) {
    const { name, pins, type, part, nextComp, nextNet, prevComp, prevNet, isGnd, key, rootId } = props;
    this.id = newNanoId(8, 'number');
    this.name = name;
    this.pins = pins;
    this.type = type;
    this.part = part;
    this.nextComp = nextComp;
    this.nextNet = nextNet;
    this.prevComp = prevComp;
    this.prevNet = prevNet;
    this.isGnd = isGnd || false;
    //key
    this.pathId = "";
    this.pcbKey = key;
    this.rootId = rootId || "";
  }
}

class RootComponent extends TreeComponent {
  constructor(props) {
    super(props);
    this.load = "";
    this.voltage = "";
    this.broComp = props.broComp;
    this.gnds = props.gnds || [];
    this.powerId = props.powerId || ""; // for isGnd
  }
}

class MiddleComponent extends TreeComponent {
  constructor(props) {
    super(props)
    this.resistance = props.resistance || "0";
  }
}

class LoadComponent extends TreeComponent {
  constructor(props) {
    super(props)
    this.load = "";
    this.vMin = "";
    this.vMax = "";
    this.broComp = props.broComp;
    this.gnds = props.gnds || [];
  }
}

class PMICComponent extends TreeComponent {
  constructor(props) {
    super(props)
    this.load = "";
    this.vMin = "";
    this.vMax = "";
    this.voltage = "";
    this.broComp = props.broComp;
    this.gnds = props.gnds || [];
  }
}

class ConnectorComponent extends TreeComponent {
  constructor(props) {
    super(props);
    this.resistance = props.resistance;
    this.broComp = props.broComp;
    this.gnds = props.gnds || [];
  }
}

export default PowerTreeMaker;